import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import './BlogPage.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8081';
const PAGE_SIZE = 10;

// Helper functions
function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { 
    month: 'long', day: 'numeric', year: 'numeric' 
  });
}

function getExcerpt(post) {
  if (post.postExcerpt) return post.postExcerpt.replace(/<[^>]*>/g, '');
  if (post.postContent) return post.postContent.replace(/<[^>]*>/g, '').slice(0, 160);
  return '';
}

function createSlug(title) {
  if (!title) return '';
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-');
}

// Blog Card Component
function BlogCard({ post }) {
  const title = post.postTitle || post.title;
  const image = post.postImage;
  const [imgError, setImgError] = useState(false);
  const slug = createSlug(title);

  return (
    <article className="blog-card-new">
      <Link to={`/${slug}`} className="blog-card-link">
        <div className="blog-card-image">
          {image && !imgError ? (
            <img src={image} alt={title} onError={() => setImgError(true)} />
          ) : (
            <div className="blog-card-image-placeholder">📷</div>
          )}
        </div>
        <div className="blog-card-content">
          <div className="blog-card-meta">
            <span className="blog-card-date">{formatDate(post.postDate)}</span>
            <span className="blog-card-category">{post.categoryName || 'Blog'}</span>
          </div>
          <h2 className="blog-card-title">{title}</h2>
          <p className="blog-card-excerpt">{getExcerpt(post)}</p>
          <span className="blog-card-readmore">Read More →</span>
        </div>
      </Link>
    </article>
  );
}

// Pagination Component
function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(0, endPage - maxVisible + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="blog-pagination">
      <button className="pagination-btn" disabled={currentPage === 0} onClick={() => onPageChange(0)}>⏮ First</button>
      <button className="pagination-btn" disabled={currentPage === 0} onClick={() => onPageChange(currentPage - 1)}>← Prev</button>
      
      <div className="pagination-numbers">
        {getPageNumbers().map(pageNum => (
          <button
            key={pageNum}
            className={`pagination-number ${currentPage === pageNum ? 'active' : ''}`}
            onClick={() => onPageChange(pageNum)}
          >
            {pageNum + 1}
          </button>
        ))}
      </div>
      
      <button className="pagination-btn" disabled={currentPage >= totalPages - 1} onClick={() => onPageChange(currentPage + 1)}>Next →</button>
      <button className="pagination-btn" disabled={currentPage >= totalPages - 1} onClick={() => onPageChange(totalPages - 1)}>Last ⏭</button>
    </div>
  );
}

// Main Search Page Component
export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const page = parseInt(searchParams.get('page')) || 0;
  
  const [allPosts, setAllPosts] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // First, fetch all posts
  useEffect(() => {
    fetchAllPosts();
  }, []);

  // Then filter when query changes
  useEffect(() => {
    if (allPosts.length > 0 && query) {
      performLocalSearch();
    }
  }, [query, allPosts, page]);

  const fetchAllPosts = async () => {
    setLoading(true);
    setError(false);
    try {
      // Fetch all published posts
      const res = await axios.get(`${API}/api/posts`, {
        params: { page: 0, size: 100 } // Fetch more posts for search
      });
      
      const posts = res.data.content || [];
      setAllPosts(posts);
      
      // If query exists, perform search
      if (query) {
        performLocalSearch(posts);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      setError(true);
      setLoading(false);
    }
  };

  const performLocalSearch = (postsToSearch = allPosts) => {
    setLoading(true);
    
    try {
      const searchTerm = query.toLowerCase();
      const filtered = postsToSearch.filter(post => {
        const title = (post.postTitle || '').toLowerCase();
        const content = (post.postContent || '').toLowerCase();
        const excerpt = (post.postExcerpt || '').toLowerCase();
        const category = (post.categoryName || '').toLowerCase();
        
        return title.includes(searchTerm) || 
               content.includes(searchTerm) || 
               excerpt.includes(searchTerm) ||
               category.includes(searchTerm);
      });
      
      // Paginate results
      const start = page * PAGE_SIZE;
      const end = start + PAGE_SIZE;
      const paginatedResults = filtered.slice(start, end);
      
      setFilteredResults(paginatedResults);
      setTotalResults(filtered.length);
      setTotalPages(Math.ceil(filtered.length / PAGE_SIZE));
      setError(false);
    } catch (err) {
      console.error('Search error:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setSearchParams({ q: query, page: newPage.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Highlight search term in text
  const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    try {
      const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      return text.replace(regex, '<mark>$1</mark>');
    } catch {
      return text;
    }
  };

  return (
    <div className="blog-page-new">
      <div className="blog-container-new">
        <main className="blog-main-new">
          <div className="blog-header-new">
            <h1>🔍 Search Results</h1>
            <p>
              {!loading && !error && query && totalResults > 0 
                ? `Found ${totalResults} result${totalResults !== 1 ? 's' : ''} for "${query}"`
                : !loading && !error && query && totalResults === 0
                ? `No results found for "${query}"`
                : !query && "Enter a search term to find posts"}
            </p>
            <Link to="/blog" className="back-to-blog">← Back to Blog</Link>
          </div>

          {loading ? (
            <div className="loading-state">🔍 Searching for "{query}"...</div>
          ) : error ? (
            <div className="error-state">
              <p>⚠️ Error performing search. Please try again.</p>
              <button onClick={() => fetchAllPosts()}>Retry</button>
            </div>
          ) : !query ? (
            <div className="empty-state">
              <p>🔍 Please enter a search term to find posts.</p>
              <Link to="/blog" className="back-to-blog">Browse all posts →</Link>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="empty-state">
              <p>🔍 No posts match your search for "{query}".</p>
              <p>💡 Try different keywords or browse all posts.</p>
              <Link to="/blog" className="back-to-blog">Browse all posts →</Link>
            </div>
          ) : (
            <>
              <div className="search-summary">
                Found {totalResults} posts matching "{query}"
              </div>
              
              {filteredResults.map(post => {
                // Create a copy with highlighted title for display
                const highlightedTitle = highlightText(post.postTitle, query);
                return (
                  <article key={post.id} className="blog-card-new">
                    <Link to={`/${createSlug(post.postTitle)}`} className="blog-card-link">
                      <div className="blog-card-image">
                        {post.postImage ? (
                          <img src={post.postImage} alt={post.postTitle} />
                        ) : (
                          <div className="blog-card-image-placeholder">📷</div>
                        )}
                      </div>
                      <div className="blog-card-content">
                        <div className="blog-card-meta">
                          <span className="blog-card-date">{formatDate(post.postDate)}</span>
                          <span className="blog-card-category">{post.categoryName || 'Blog'}</span>
                        </div>
                        <h2 className="blog-card-title" dangerouslySetInnerHTML={{ __html: highlightedTitle }} />
                        <p className="blog-card-excerpt" dangerouslySetInnerHTML={{ __html: highlightText(getExcerpt(post), query) }} />
                        <span className="blog-card-readmore">Read More →</span>
                      </div>
                    </Link>
                  </article>
                );
              })}
              
              {totalPages > 1 && (
                <Pagination 
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
              
              <div className="pagination-info">
                Showing {filteredResults.length} of {totalResults} results for "{query}"
              </div>
            </>
          )}
        </main>

        <aside className="blog-sidebar-new">
          <div className="sidebar-widget-new">
            <h3>🔍 New Search</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const input = e.target.querySelector('input');
              const newQuery = input.value.trim();
              if (newQuery) {
                window.location.href = `/search?q=${encodeURIComponent(newQuery)}`;
              }
            }}>
              <input 
                type="text" 
                defaultValue={query}
                placeholder="Search articles..." 
                className="sidebar-search-input"
              />
              <button type="submit" style={{ 
                marginTop: '12px', 
                width: '100%', 
                padding: '12px', 
                background: 'linear-gradient(135deg, #c5a059, #a8883e)', 
                color: 'white', 
                border: 'none', 
                borderRadius: '12px', 
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}>
                🔍 Search →
              </button>
            </form>
          </div>
          
          <div className="sidebar-widget-new">
            <h3>💡 Search Tips</h3>
            <ul style={{ fontSize: '13px', color: '#666', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>🔎 Use specific keywords like "tax", "legal", "corporate"</li>
              <li>📝 Search in post titles, content, and categories</li>
              <li>🏷️ Try browsing categories for similar content</li>
              <li>⭐ Popular posts appear in the sidebar</li>
            </ul>
          </div>
          
          <div className="sidebar-widget-new">
            <h3>📁 Popular Categories</h3>
            <div className="categories-list-simple">
              <Link to="/category/tax-matters" className="category-item-link">
                <span className="category-icon">💰</span>
                <span className="category-name">Tax Matters</span>
              </Link>
              <Link to="/category/legal-matters" className="category-item-link">
                <span className="category-icon">⚖️</span>
                <span className="category-name">Legal Matters</span>
              </Link>
              <Link to="/category/corporate" className="category-item-link">
                <span className="category-icon">🏢</span>
                <span className="category-name">Corporate</span>
              </Link>
              <Link to="/category/immigration" className="category-item-link">
                <span className="category-icon">🛂</span>
                <span className="category-name">Immigration</span>
              </Link>
              <Link to="/category/business" className="category-item-link">
                <span className="category-icon">🏠</span>
                <span className="category-name">Business</span>
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}